import { type InferResponseType, unwrap } from "@nexxt/api-client";
import type { ListCodeMappingsInput } from "@nexxt/contracts/reference";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the codes of the other systems (50-api/reference.md): the records of a domain by page, and how
// many each domain still has without a code.

const mappings = api.reference["code-mappings"];

type ListPage = InferResponseType<typeof mappings.$get, 200>;
export type CodeMapping = ListPage["items"][number];

/** What a list is of: a system and a domain, for every brand or for one; then the filters. */
export type MappingFilters = Pick<ListCodeMappingsInput, "system" | "domain" | "brandOrgId" | "unmapped" | "q">;

/** What the screen opens on: the system and the data the first messages name. */
export const DEFAULT_SYSTEM: MappingFilters["system"] = "business_central";
export const DEFAULT_DOMAIN: MappingFilters["domain"] = "season";

const PAGE_SIZE = 100;

const keys = { all: ["code-mappings"] as const };

export const codeMappingQueries = {
  ...keys,

  list: ({ system, domain, brandOrgId, unmapped, q }: MappingFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.all, "list", { system, domain, brandOrgId, unmapped, q }],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          mappings.$get(
            {
              query: {
                system,
                domain,
                ...(brandOrgId && { brandOrgId }),
                ...(unmapped && { unmapped: "true" }),
                ...(q && { q }),
                page: String(pageParam),
                pageSize: String(PAGE_SIZE),
              },
            },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  summary: ({ system, brandOrgId }: Pick<MappingFilters, "system" | "brandOrgId">) =>
    queryOptions({
      queryKey: [...keys.all, "summary", { system, brandOrgId }],
      queryFn: ({ signal }) =>
        unwrap(mappings.summary.$get({ query: { system, ...(brandOrgId && { brandOrgId }) } }, { init: { signal } })),
    }),
};
